import { View } from "@pimo/pimo-app-builder";
import {
  PMDescriptionCard,
  PMSmallNumber,
  PMSmallTable,
} from "@pimo/pimo-components";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { getContrastColor } from "vmd-utils";

import { TestAppState } from "../../app";
import { RECORD_TYPE_BADGE_COLORS } from "../../constants/colors";
import { theme } from "../../theme";
import { formatDate } from "../../utils/format-date";
import { toDisplayNumber } from "../../utils/to-display-number";

/**
 * The dynamic cards `buildDynamicallyBasedOnAppState` adds on top of the
 * static ones from `./index` (#2222): one per enabled `projectReporting`
 * custom field, plus a sum/average card for any custom field the backend has
 * pre-aggregated (`customFieldSums`/`customFieldAverages`).
 */
export function addCustomFieldCards(
  view: View<TestAppState, DragAndDropLayout>,
  state: {
    configuration?: NonNullable<TestAppState["settings"]>["configuration"];
    project?: NonNullable<TestAppState["currentReport"]>["project"];
  }
) {
  for (const customField of state.configuration?.customFields?.filter(
    (f) => f?.enabled && f?.purpose === "projectReporting"
  ) ?? []) {
    const matchingCustomFieldValue = state.project?.customFields?.find(
      (f) => f.customFieldDefinition.id === customField.id
    );
    switch (customField.type) {
      case "Text": {
        const customFieldCardText = view.addComponent({
          component: PMDescriptionCard,
          layoutProps: {
            componentId: `customField-${customField.id}-${customField.label}`,
            description: customField.label,
            title: customField.label,
            labels: [customField.label],
          },
        });

        customFieldCardText.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          description: customField.label,
          text: matchingCustomFieldValue?.value ?? "",
        }));
        break;
      }
      case "Number": {
        const customFieldCard = view.addComponent({
          component: PMSmallNumber,
          layoutProps: {
            componentId: `customField-${customField.id}-${customField.label}`,
            labels: [customField.label, "summaryDashboard"],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldCard.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          number: toDisplayNumber(matchingCustomFieldValue?.value),
        }));
        break;
      }
      case "Date": {
        const customFieldDate = view.addComponent({
          component: PMSmallTable,
          layoutProps: {
            componentId: customField.id,
            labels: [customField.label],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldDate.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          items: [
            {
              label: customField.label,
              value: matchingCustomFieldValue?.value
                ? formatDate(matchingCustomFieldValue?.value)
                : "n/a",
              chipColor: theme.palette.secondary.main,
              chipTextColor: theme.palette.primary.main,
            },
          ],
          fixedHeight: "134px",
        }));
        break;
      }
      case "Select": {
        const customFieldSelect = view.addComponent({
          component: PMSmallTable,
          layoutProps: {
            componentId: `customField-${customField.id}-${customField.label}`,
            labels: [customField.label],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldSelect.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          items: [
            {
              label: matchingCustomFieldValue?.value ?? "n/a",
              value: matchingCustomFieldValue?.value ?? "n/a",
              chipColor:
                customField.options?.find(
                  (option) => option.name === matchingCustomFieldValue?.value
                )?.color ?? RECORD_TYPE_BADGE_COLORS.itService,
              chipTextColor: getContrastColor(
                customField.options?.find(
                  (option) => option.name === matchingCustomFieldValue?.value
                )?.color ?? RECORD_TYPE_BADGE_COLORS.itService
              ),
            },
          ],
          fixedHeight: "134px",
        }));
        break;
      }
    }
  }

  const customFieldSums = state.project?._calculated?.customFieldSums;

  for (const [recordId, sumValue] of Object.entries(customFieldSums ?? {})) {
    const matchingCustomFieldDefinition =
      state.configuration?.customFields?.find(
        (customField) => String(customField.id) === String(recordId)
      );

    if (!matchingCustomFieldDefinition) {
      continue;
    }

    const customFieldSumTextCard = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: `customField-sum-${recordId}-${matchingCustomFieldDefinition.label}`,
        labels: [matchingCustomFieldDefinition.label, "customFieldSums"],
        title: `Sum of ${matchingCustomFieldDefinition.label}`,
        description: `Project sum for ${matchingCustomFieldDefinition.label}`,
      },
    });

    customFieldSumTextCard.mapState(() => ({
      showInfoIcon: false,
      showMenuIcon: false,
      title: `Sum of ${matchingCustomFieldDefinition.label}`,
      description: `Project sum for ${matchingCustomFieldDefinition.label}`,
      number: toDisplayNumber(sumValue),
    }));
  }

  for (const [recordId, averageValue] of Object.entries(
    state.project?._calculated?.customFieldAverages ?? {}
  )) {
    const matchingCustomFieldDefinition =
      state.configuration?.customFields?.find(
        (customField) => String(customField.id) === String(recordId)
      );

    if (!matchingCustomFieldDefinition) {
      continue;
    }

    const customFieldAverageTextCard = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: `customField-average-${recordId}-${matchingCustomFieldDefinition.label}`,
        labels: [matchingCustomFieldDefinition.label, "customFieldAverages"],
        description: `Project average for ${matchingCustomFieldDefinition.label}`,
        title: `Project average for ${matchingCustomFieldDefinition.label}`,
      },
    });

    customFieldAverageTextCard.mapState(() => ({
      showInfoIcon: false,
      showMenuIcon: false,
      title: `Average of ${matchingCustomFieldDefinition.label}`,
      description: `Project average for ${matchingCustomFieldDefinition.label}`,
      number: toDisplayNumber(averageValue),
    }));
  }
}
